import React, { FC, ReactElement } from 'react';
import { formatNumber as humanizeNumber } from 'humanize-plus';

import SellsideConsensus from 'buyside/components/presentation/SellsideConsensus/sellsideConsensus';
import { StyleProps } from 'buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';

import { chunk } from 'buyside/util/arrayHelper';
import formatNumber from 'buyside/util/formatNumber';

import { Fundamental, Maybe } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/IdeaShowPage/RightSidebar/PositionInformation/positionInformationStyles.scss';

const Fundamentals: FC<StyleProps & {
  currencyName: string;
  range: string;
  fundamental: Maybe<
    Pick<
      Fundamental,
      | 'id'
      | 'bookValue'
      | 'ev'
      | 'marketCap'
      | 'sellsideConsensus'
      | 'threeMonthAdv'
      | 'totalDebt'
      | 'totalCash'
    >
  >;
  optionalAsterisk: string;
}> = ({ currencyName, dataStyle, labelStyle, range, fundamental, optionalAsterisk }) => {
  const { bookValue, ev, marketCap, sellsideConsensus, threeMonthAdv, totalCash, totalDebt } =
    fundamental || {};

  return (
    <table className={styles.table}>
      <tbody>
        {chunk(
          [
            {
              label: 'CURRENCY',
              data: `${currencyName}${optionalAsterisk}`
            },
            {
              label: '52 WK. RANGE',
              data: range
            },
            {
              label: 'MARKET CAP',
              data: marketCap
                ? `${formatNumber(
                    marketCap,
                    false,
                    marketCap < 1000000000 ? 0 : 1
                  )} USD${optionalAsterisk}`
                : 'N/A'
            },
            {
              label: 'EV',
              data: ev
                ? `${formatNumber(ev, true, ev < 1000000000 ? 0 : 2)} USD${optionalAsterisk}`
                : 'N/A'
            },
            {
              label: 'TOTAL CASH',
              data: totalCash
                ? `${formatNumber(
                    totalCash,
                    true,
                    totalCash < 1000000000 ? 0 : 2
                  )} USD${optionalAsterisk}`
                : 'N/A'
            },
            {
              label: 'TOTAL DEBT',
              data: totalDebt
                ? `${formatNumber(
                    totalDebt,
                    true,
                    totalDebt < 1000000000 ? 0 : 2
                  )} USD${optionalAsterisk}`
                : 'N/A'
            },
            {
              label: 'BOOK VALUE PER SHARE',
              data: bookValue ? `${bookValue}${optionalAsterisk}` : 'N/A'
            },
            {
              label: 'THREE MO. AVG. DAILY VOLUME',
              data: threeMonthAdv ? `${humanizeNumber(threeMonthAdv)}${optionalAsterisk}` : 'N/A'
            },
            {
              label: 'SELLSIDE CONSENSUS',
              data: <SellsideConsensus sellsideConsensus={sellsideConsensus} />
            }
          ],
          2
        ).map(
          ([first, second], idx): ReactElement => (
            <tr key={idx}>
              <td>
                <div className={labelStyle}>{first.label}</div>
                <div className={dataStyle}>{first.data}</div>
              </td>
              {second && (
                <td>
                  <div className={labelStyle}>{second.label}</div>
                  <div className={dataStyle}>{second.data}</div>
                </td>
              )}
            </tr>
          )
        )}
      </tbody>
    </table>
  );
};

export default Fundamentals;
